import {
    AfterContentInit,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList
} from '@angular/core';
import { TabComponent } from './tab.component';
import { BehaviorSubject, Subscription, tap } from 'rxjs';

@Component({
    selector: 'sag-ui-tabview',
    template: `<nav sag-ui-tabs-base>
        <ul sag-ui-tabs-list [fullWidth]="fullWidth" [tabsCount]="tabs.length">
            <ng-content select="sag-ui-tab"></ng-content>
        </ul>
        @for (tab of tabs; track tab; let i = $index) { @if (tab.active) {
        <sag-ui-tab-content [template]="tab.tabContentTemplate" [slide]="$any(slide$ | async)"></sag-ui-tab-content>
        } }
    </nav>`
})
export class TabViewComponent implements AfterContentInit, OnDestroy {
    @Input()
    set value(val: string | number) {
        this._value = val;
        this.updateTabs();
    }

    @Input() fullWidth = false;

    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() change: EventEmitter<string | number> = new EventEmitter();

    @ContentChildren(TabComponent)
    tabs!: QueryList<TabComponent>;

    private _value: string | number = 0;
    private tabList: TabComponent[] = [];
    private subscriptions: Subscription[] = [];
    private previousIndex = 0;
    protected slide$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    public ngAfterContentInit(): void {
        // Prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.updateTabs();
            this.subscribeToTabChanges();
        }, 0);
    }

    public ngOnDestroy(): void {
        this.resetSubscriptions();
    }

    private updateTabs(): void {
        if (!this.tabs) return;

        this.tabList = this.tabs.toArray();
        const initialTab = this.tabList
            .filter(tab => !tab.disabled)
            .find((tab, index) => tab.value === this._value || index === this._value);

        if (initialTab) {
            this.activateTab(initialTab);
        } else {
            const defaultTab = this.tabList.filter(tab => !tab.disabled)[0];
            this.activateTab(defaultTab);
        }
    }

    private subscribeToTabChanges(): void {
        this.tabs.forEach(tab => {
            this.subscriptions.push(
                tab.click
                    .pipe(
                        tap(value => {
                            this.change.emit(value);
                            this.activateTab(tab);
                            this.updateSlideAnimation();
                        })
                    )
                    .subscribe()
            );
        });
    }

    private activateTab(activeTab: TabComponent): void {
        this.tabs.forEach(tab => {
            tab.active = tab === activeTab;
        });
    }

    private updateSlideAnimation(): void {
        const currentIndex = this.tabList.findIndex(tab => tab.active);
        const slide = currentIndex > this.previousIndex;
        this.previousIndex = currentIndex;
        this.slide$.next(slide);
    }

    private resetSubscriptions(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }
}
